import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CallWithAuth } from '../../actions/apiAction';
import { FEEDBACK_URL } from '../../utils/apiUrl';

const FEEDBACK_TYPES = ['Text quality', 'Call quality', 'Video quality', 'Connection issues', 'Other'];

const FeedbackForm = ({ onClose }) => {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [type, setType] = useState('');
    const [message, setMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await CallWithAuth('POST', FEEDBACK_URL, {
                rating: rating || null,
                category: type || null,
                comments: message || null
            });
            if (response.status === 201) {
                setSubmitted(true);
            } else {
                toast.error('Failed to send feedback');
            }
        } catch {
            toast.error('Failed to send feedback');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) return (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                    background: 'linear-gradient(135deg, #AF0056, #FF4DA6)',
                    animation: 'feedbackPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both'
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={30}
                    height={30}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ animation: 'feedbackCheckDraw 0.4s ease 0.3s both' }}
                >
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            </div>
            <p className="heading-text mt-1">Thanks for your feedback!</p>
            <p className="text-[var(--black)] text-sm">We'll get right on this.</p>
            {onClose && (
                <button
                    onClick={onClose}
                    className="mt-3 px-8 py-2.5 rounded-full text-white font-medium text-sm"
                    style={{ background: 'linear-gradient(135deg, #AF0056, #FF4DA6)' }}
                >
                    Done
                </button>
            )}
            <style>{`
                @keyframes feedbackPop {
                    0% { transform: scale(0); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes feedbackCheckDraw {
                    0% { opacity: 0; transform: scale(0.5); }
                    100% { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );

    return (
        <div className="">
            {onClose && (
                <button
                    className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={onClose}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                </button>
            )}
            <p className="heading-text mb-1 pr-6">We'd love your feedback</p>
            <p className="text-[var(--black)] text-sm mb-3">A few seconds of your time means a lot</p>

            {/* Star rating */}
            <p className="text-[var(--black)] text-xs tracking-wider mb-2">Overall, how are we doing?</p>
            <div className="flex gap-2 mb-4">
                {[1, 2, 3, 4, 5].map(i => (
                    <button key={i}
                        className="text-2xl transition-transform text-[var(--black)]"
                        style={{ filter: i <= (hoverRating || rating) ? 'none' : 'grayscale(1) opacity(0.5)', transform: i <= (hoverRating || rating) ? 'scale(1.2)' : 'scale(1)' }}
                        onClick={() => setRating(i)}
                        onMouseEnter={() => setHoverRating(i)}
                        onMouseLeave={() => setHoverRating(0)}
                    >⭐</button>
                ))}
            </div>

            {/* Type */}
            <p className="text-[var(--black)] text-xs tracking-wider mb-2">What could we do better?</p>
            <div className="flex flex-wrap gap-2 mb-3">
                {FEEDBACK_TYPES.map(t => (
                    <button key={t}
                        onClick={() => setType(t)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${type === t ? '!border-[#ff4da6] text-[white] bg-[#ff4da6]' : '!border-[#000] text-[var(--black)]'}`}
                    >{t}</button>
                ))}
            </div>


            {/* Message */}
            <p className="text-[#000] text-xs tracking-wider mb-2">Tell us more</p>
            <textarea
                rows={3}
                placeholder="What's on your mind?"
                className="w-full bg-white/5 border !border-[#000] rounded-xl text-[#000] text-sm px-3 py-2.5 resize-none outline-none focus:!border-[#ff4da6] placeholder:text-[#888]"
                value={message}
                onChange={e => setMessage(e.target.value)}
            />

            <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full mt-3 py-2.5 rounded-full text-white font-medium text-sm disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #AF0056, #FF4DA6)' }}
            >
                {loading ? 'Submitting...' : 'Submit'}
            </button>
        </div>
    );
};

export default FeedbackForm;